import { useCallback } from 'react';
import { useAppRouter } from '../useAppRouter';
import { getPublicRuntimeConfig } from '../../libs/init/getPublicRuntimeConfig';
import { getLogger } from '../../libs/logger';
import { useAppSelector } from '../../hooks/useReduxToolkit';
import { getApolloSessionDetails } from '../../features/apolloSession/apolloSession.selectors';
import { getCheckInRedirectRoute } from './helpers';
import { getGrowthBookConfigLoader } from '@doxyme/config';

const logger = getLogger('useRedirect');

export type UseRedirectReturnType = {
  redirectToWaitingRoom: (roomSlug: string) => void;
  getCheckInRedirectRoute: (
    roomSlug: string | undefined | null,
  ) => string | undefined;
  redirectToCheckInPage: () => void;
  openInSeparateTab: (url: string) => void;
  redirectToWrongRoomPage: (roomSlug?: string) => void;
  redirectToWrongClinicPage: () => void;
  redirectToNotAvailablePage: () => void;
  redirectToDashboardPage: (hasSurvey?: boolean) => void;
  redirectToCheckInPageFromCallPage: () => void;
  redirectToCheckInPageFromHoldPage: () => void;
};

/**
 * Incapsulates the next router logic to make page redirects.
 */
export const useRedirect = (): UseRedirectReturnType => {
  const router = useAppRouter();
  const { referrer } = useAppSelector(getApolloSessionDetails);
  const redirectToWaitingRoom = (roomSlug: string): void => {
    void router.push({
      pathname: '/waiting-room',
      query: {
        slug: roomSlug,
      },
    });
  };

  const redirectToCheckInPage = (): void => {
    const { slug } = router.query;

    if (!String(slug) || Array.isArray(slug)) {
      return;
      // @TODO: redirect to 404 Not found page
    }

    if (Boolean(slug) && String(slug)) {
      void router.push({
        pathname: `/check-in/${slug as string}`,
      });
    }
  };

  /**
   * Redirect patient from call page to check-in.
   */
  const redirectToCheckInPageFromCallPage = (): void => {
    const prefix = '[redirect/redirectToCheckInPageFromCallPage]';

    if (!referrer) {
      logger.warn(prefix, 'missing referrer');
      return;
    }

    /**
     * Uses window.location.href instead of router.push to reset patient state
     */
    window.location.href = referrer;
  };

  /**
   * Redirect patient from hold page to check-in.
   */
  const redirectToCheckInPageFromHoldPage = (): void => {
    const prefix = '[redirect/redirectToCheckInPageFromHoldPage]';

    if (!referrer) {
      logger.warn(prefix, 'missing referrer');
      return;
    }

    /**
     * Uses window.location.href instead of router.push to reset patient state
     */
    window.location.href = referrer;
  };

  const openInSeparateTab = useCallback((url: string): void => {
    window.open(url, '_blank')?.focus();
  }, []);

  const getIsV2WrongRoomOrClinicEnabled = useCallback(
    (): boolean =>
      getGrowthBookConfigLoader().getOptional(
        'feat_prod_5524_v2_wrong_room_and_clinic_page',
      ) === true,
    [],
  );

  const redirectToWrongRoomPage = useCallback(
    (
      roomSlug?: string,
      /**
       *  * @param _window - Internal parameter for Dependency Injection [DO NOT USE] -
       */
      _window: Window = window,
    ): void => {
      if (getIsV2WrongRoomOrClinicEnabled()) {
        logger.debug('[redirect/redirectToV2WrongRoomPage] Debug', roomSlug);

        const { paths } = getPublicRuntimeConfig();

        const optionalQueryParam = roomSlug
          ? { query: { slug: roomSlug } }
          : {};

        void router.push({
          pathname: paths.wrongRoom,
          ...optionalQueryParam,
        });
      } else {
        logger.debug('[redirect/redirectToOgWrongRoomPage] Debug', roomSlug);
        const optionalQueryParam = roomSlug ? `?slug=${roomSlug}` : '';
        _window.location.href = '/wrong-room' + optionalQueryParam;
      }
    },
    [getIsV2WrongRoomOrClinicEnabled, router],
  );

  const redirectToWrongClinicPage = useCallback(
    (
      /**
       *  * @param _window - Internal parameter for Dependency Injection [DO NOT USE] -
       */
      _window: Window = window,
    ): void => {
      if (getIsV2WrongRoomOrClinicEnabled()) {
        logger.debug('[redirect/redirectToV2WrongClinicPage] Debug');

        const { paths } = getPublicRuntimeConfig();
        void router.push({
          pathname: paths.wrongClinic,
        });
      } else {
        // As OG doesn't have a wrong-clinic page, redirect instead to the OG wrong room page
        logger.debug('[redirect/redirectToOgWrongRoomPage] Debug');
        _window.location.href = `/wrong-room`;
      }
    },
    [getIsV2WrongRoomOrClinicEnabled, router],
  );

  const redirectToNotAvailablePage = useCallback((): void => {
    logger.debug('[redirect/redirectToNotAvailablePage] Debug');

    const { paths } = getPublicRuntimeConfig();
    void router.push({
      pathname: paths.notAvailable,
    });
  }, [router]);

  const redirectToDashboardPage = useCallback((hasSurvey = true): void => {
    logger.debug('[redirect/redirectToDashboardPage] Debug');

    const { paths } = getPublicRuntimeConfig();

    void router.push({
      pathname: paths.dashboard,
      ...(hasSurvey && {
        query: {
          survey: 'true',
        },
      }),
    });
  }, []);

  return {
    getCheckInRedirectRoute,
    redirectToWaitingRoom,
    redirectToCheckInPage,
    redirectToWrongRoomPage,
    redirectToWrongClinicPage,
    redirectToNotAvailablePage,
    redirectToDashboardPage,
    openInSeparateTab,
    redirectToCheckInPageFromCallPage,
    redirectToCheckInPageFromHoldPage,
  };
};
